import { Link } from "react-router-dom";
import { toast } from "sonner";
import { User, KeyRound, Shield, LogOut, ChevronRight, FileDown } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { SETTINGS } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";

const ROWS = [
  { to: "/app/settings/profile", icon: User, title: "Profile", desc: "Update your name, mobile, and designation." },
  { to: "/app/settings/change-password", icon: KeyRound, title: "Change password", desc: "Rotate your password. Recommended every 90 days." },
];

export default function AccountSettingsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role_code === "super_admin";

  const downloadSOP = async () => {
    try {
      const resp = await api.get("/admin/sop/download", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const d = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `Steward-LMS-SOP-${d}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("SOP PDF downloaded.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <DashboardLayout title="Account settings" breadcrumbs={["Settings"]}>
      <div data-testid={SETTINGS.accountRoot} className="max-w-3xl space-y-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#101876] text-lg font-black text-white">
              {(user?.first_name?.[0] || "S") + (user?.last_name?.[0] || "")}
            </div>
            <div>
              <div className="text-lg font-bold text-slate-900">{user?.full_name}</div>
              <div className="text-sm text-slate-500">{user?.email}</div>
              <div className="mt-1 text-[11px] font-semibold uppercase tracking-widest text-[#329112]">{user?.role_name} · {user?.organization_name}</div>
            </div>
          </div>
        </div>

        {isSuperAdmin && (
          <div className="rounded-2xl border border-slate-200 bg-gradient-to-r from-[#101876]/5 to-[#329112]/5 p-5 flex items-start justify-between gap-4" data-testid="sop-card">
            <div className="min-w-0">
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Platform Documentation</div>
              <h3 className="text-base font-bold text-slate-900 mt-0.5">Standard Operating Procedures (SOP)</h3>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                Full operations handbook covering every LMS module. <b>Confidential — Super Admin only.</b>
              </p>
            </div>
            <button
              type="button"
              onClick={downloadSOP}
              className="shrink-0 inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c]"
              data-testid="account-sop-download-btn"
            >
              <FileDown className="h-4 w-4" /> Download SOP (PDF)
            </button>
          </div>
        )}

        <div className="divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          {ROWS.map((r) => (
            <Link key={r.to} to={r.to} className="flex items-center gap-4 p-5 transition hover:bg-slate-50">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#101876]/5 text-[#101876]">
                <r.icon className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold text-slate-900">{r.title}</div>
                <div className="text-xs text-slate-500">{r.desc}</div>
              </div>
              <ChevronRight className="h-4 w-4 text-slate-400" />
            </Link>
          ))}
          <div className="flex items-center gap-4 p-5 opacity-70">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-500"><Shield className="h-4 w-4" /></div>
            <div className="flex-1">
              <div className="text-sm font-bold text-slate-900">Two-factor authentication</div>
              <div className="text-xs text-slate-500">Available from Phase 12 alongside SMS and WhatsApp.</div>
            </div>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Phase 12</span>
          </div>
        </div>

        <button
          onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
          className="inline-flex items-center gap-2 rounded-md border border-rose-200 bg-white px-4 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50"
          data-testid="settings-logout-btn"
        >
          <LogOut className="h-4 w-4" /> Log out of Steward
        </button>
      </div>
    </DashboardLayout>
  );
}
